import {Injectable} from "@angular/core";
import {AbstractService} from "../abstract.service";
import {InvoiceRepo} from "../../repositories/api/invoice.repo";
import {map} from "rxjs/operators";
import {Additionals} from "../../repositories/item/additionals";
import {InvoiceDto} from "../../repositories/dto/document/invoice.dto";
import {InvoicePositionDto} from "../../repositories/dto/document/invoice-position.dto";
import {InvoiceState} from "../../libs/enum/invoice-state";
import {Observable} from "rxjs";
import {VoidDto} from "../../repositories/dto/void.dto";
import {SearchFilterRequest, SearchRequest} from "../../repositories/dto/search/search-table.dto.req";
import {SearchInvoiceRes} from "../../repositories/dto/search/search-invoice.res";
import {DocumentContactDto} from "../../repositories/dto/document-contact.dto";
import {DeliveryNoteDto} from "../../repositories/dto/delivery-note.dto";
import {SubInvoiceDto} from "../../repositories/dto/sub-invoice.dto";
import {HttpResponse} from "@angular/common/http";
import {DocumentPropertyDto} from "../../repositories/dto/document-property.dto";

@Injectable({providedIn: 'root'})
export class InvoiceService extends AbstractService {

  private readonly invoiceRepo: InvoiceRepo = this.repoProvider.getRepo(InvoiceRepo);

  public getById(invoiceId: string, add?: Additionals): Observable<InvoiceDto> {
    return this.invoiceRepo.getById(this.getOrg(), invoiceId, add)
      .pipe(map((w) => w.getFirstData()));
  }

  public update(invoice: InvoiceDto, add?: Additionals): Observable<InvoiceDto> {
    return this.invoiceRepo.update(this.getOrg(), invoice, add)
      .pipe(map((w) => w.getFirstData()));
  }

  public delete(invoiceId: string): Observable<VoidDto> {
    return this.invoiceRepo.delete(this.getOrg(), invoiceId)
      .pipe(map((w) => w.getFirstData()));
  }

  public getContactById(invoiceId: string, documentContactId: string): Observable<DocumentContactDto> {
    return this.invoiceRepo.getContactById(this.getOrg(), invoiceId, documentContactId)
      .pipe(map((w) => w.getFirstData()));
  }

  public updateProperty(invoiceId: string, property: DocumentPropertyDto): Observable<DocumentPropertyDto> {
    return this.invoiceRepo.updateProperty(this.getOrg(), invoiceId, property)
      .pipe(map((w) => w.getFirstData()));
  }

  // ##########################################################################
  // #### Special actions
  // ##########################################################################

  public getSubInvoiceContact(invoiceId: string, subInvoice: SubInvoiceDto): Observable<DocumentContactDto> {
    if (subInvoice.isDeliveryNote()) {
      return this.invoiceRepo.getDeliveryNoteContact(this.getOrg(), invoiceId, subInvoice.id)
        .pipe(map((w) => w.getFirstData()));
    } else if (subInvoice.isProformaInvoice()) {
      return this.invoiceRepo.getProformaInvoiceContact(this.getOrg(), invoiceId, subInvoice.id)
        .pipe(map((w) => w.getFirstData()));
    }
    throw new Error("Sub-Invoice type not implemented");
  }

  public changeState(invoiceId: string, state: InvoiceState, add?: Additionals): Observable<InvoiceDto> {
    return this.invoiceRepo.changeState(this.getOrg(), invoiceId, state, add)
      .pipe(map((w) => w.getFirstData()));
  }

  public createDeliveryNote(invoiceId: string): Observable<DeliveryNoteDto> {
    return this.invoiceRepo.createDeliveryNote(this.getOrg(), invoiceId)
      .pipe(map((w) => w.getFirstData()));
  }

  public createProformaInvoice(invoiceId: string): Observable<DeliveryNoteDto> {
    return this.invoiceRepo.createProformaInvoice(this.getOrg(), invoiceId)
      .pipe(map((w) => w.getFirstData()));
  }

  public createCancellation(invoiceId: string): Observable<InvoiceDto> {
    return this.invoiceRepo.createCancellation(this.getOrg(), invoiceId)
      .pipe(map((w) => w.getFirstData()));
  }

  public createPreview(invoiceId: string): Observable<HttpResponse<Blob>> {
    return this.invoiceRepo.createPreview(this.getOrg(), invoiceId);
  }

  public createDraft(invoiceId: string): Observable<InvoiceDto> {
    return this.invoiceRepo.createDraft(this.getOrg(), invoiceId)
      .pipe(map((w) => w.getFirstData()));
  }

  public getHistoryByContact(contactId: string): Observable<SearchInvoiceRes> {
    const request = new SearchRequest({
      query: '', page: 1, pageSize: 100, sortField: 'invoiceDate', sortOrder: 'desc',
      fields: ['id', 'state', 'number', 'invoiceDate', 'totalNetAmount', 'currencyCode', 'fractionalDigits'],
      filters: [
        new SearchFilterRequest({pos: 1001, filterValueField: 'state', selected: ['INVOICE']}),
        new SearchFilterRequest({pos: 1002, filterValueField: 'dcContactId', selected: [contactId]}),
      ]
    });
    return this.invoiceRepo.search(this.getOrg(), request)
      .pipe(map((w) => w.getFirstData()));
  }

  public getHistoryByVehicle(vehicleId: string): Observable<SearchInvoiceRes> {
    const request = new SearchRequest({
      query: '', page: 1, pageSize: 100, sortField: 'invoiceDate', sortOrder: 'desc',
      fields: ['id', 'state', 'number', 'invoiceDate', 'totalNetAmount', 'currencyCode', 'fractionalDigits'],
      filters: [
        new SearchFilterRequest({pos: 1001, filterValueField: 'state', selected: ['INVOICE']}),
        new SearchFilterRequest({pos: 1003, filterValueField: 'dcContactVehicleId', selected: [vehicleId]}),
      ]
    });
    return this.invoiceRepo.search(this.getOrg(), request)
      .pipe(map((w) => w.getFirstData()));
  }

  // ##########################################################################
  // #### Positions
  // ##########################################################################

  public updatePos(invoiceId: string, pos: InvoicePositionDto): Observable<InvoiceDto> {
    return this.invoiceRepo.updatePos(this.getOrg(), invoiceId, pos)
      .pipe(map((w) => w.getFirstData()));
  }

  public deletePos(invoiceId: string, pos: InvoicePositionDto): Observable<InvoiceDto> {
    return this.invoiceRepo.deletePos(this.getOrg(), invoiceId, pos)
      .pipe(map((w) => w.getFirstData()));
  }

  public movePos(invoiceId: string, pos: InvoicePositionDto, positionTarget: number): Observable<InvoiceDto> {
    return this.invoiceRepo.movePos(this.getOrg(), invoiceId, pos, positionTarget)
      .pipe(map((w) => w.getFirstData()));
  }

  public addArticleAsPos(invoiceId: string, articleId: string): Observable<InvoiceDto> {
    return this.invoiceRepo.addArticleAsPos(this.getOrg(), invoiceId, articleId)
      .pipe(map((w) => w.getFirstData()));
  }
}
